import { unwrapResult } from '@reduxjs/toolkit';
import { message, Popover } from 'antd';
import clsx from 'clsx';
import Picker from '@emoji-mart/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { ChatMessageBlockType, UniqueVisitor, User } from 'sb-lib/types';
import { getCannedResponses } from 'shared/api';
import { ConversationVisitorEmailReply } from 'shared/components/ConversationVisitorEmailReply';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { sendChatMessage, sendChatTyping } from 'shared/store/chat-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { CannedResponse } from 'types/canned-responses';
import { ActiveVisitor } from 'types/client';
import UserAvatar from '../UserAvatar';
import emojiData from './emoji-mart-data.json';
import { SendInputCollectorButton } from './SendInputCollectorButton';
import { SendMeetingBookerButton } from './SendMeetingBookerButton';
import { SessionChatUploadButton } from './SessionChatUploadButton';
import { Button } from '../Button';
import { Icon } from '../Icon';
import './style.less';

interface Props {
  visitor: ActiveVisitor | UniqueVisitor | null;
  size?: 'default' | 'collapsed';
  height?: number | 'auto';
  fullWidth?: boolean;
  autoFocus?: boolean;
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

export const SessionChatFooter: React.FC<Props> = ({
  visitor,
  size = 'default',
  height = 116,
  fullWidth = false,
  autoFocus,
  handleImageChange,
}) => {
  const dispatch = useAppDispatch();
  const self = useAppSelector(selectActiveUser);
  const { conversation } = useAppSelector(s => s.conversation);
  const members = useAppSelector(selectActiveOrgMembers);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState<boolean | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([
    { id: '', code: '', content: '' },
  ]);
  const [isSmileyPopoverVisible, setIsSmileyPopoverVisible] = useState(false);

  const cannedResponseList = useMemo(() => {
    return cannedResponses.map(m => ({ display: m.code, ...m }));
  }, [cannedResponses]);

  const mentionees = useMemo(() => {
    return members.map(m => ({ display: m.displayName, ...m }));
  }, [members]);

  const fetchCannedResponses = useCallback(async () => {
    let res: CannedResponse[];
    try {
      res = (await getCannedResponses()).data;
      if (res && res.length > 0) {
        setCannedResponses(res);
      }
    } catch (err) {
      console.error('Failed to fetch canned responses', undefined, err);
    }
  }, []);

  useEffect(() => {
    fetchCannedResponses();
  }, [fetchCannedResponses]);

  useEffect(() => {
    if (typeof isTyping === 'boolean' && conversation?.id) {
      dispatch(sendChatTyping({ isTyping, conversationId: conversation.id }));
    }
  }, [isTyping, dispatch, conversation?.id]);

  const handleChange = useCallback(
    (event, newValue, __, mentions) => {
      if (newValue.includes('**[')) {
        // matches the start of a canned response from the Mention component as defined in the markup tag.
        const index = mentions[0].id;
        const content = cannedResponses.find(
          cannedResponse => cannedResponse.code == index,
        );
        if (content) {
          setText(content.content);
        }
      } else {
        setMentionedUserIds(mentions.map((m: { id: number }) => m.id));
        setText(event.target.value);
      }
      const isTyping = !!event.target.value;
      setIsTyping(isTyping);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      }
    },
    [cannedResponses],
  );

  const handleKeyPress = useCallback(
    async event => {
      if (!self || !visitor || !conversation?.id || !text || !text.trim().length) return;
      if ('key' in event && (event.key !== 'Enter' || event.shiftKey)) return;
      event.preventDefault();
      const FULL_MENTION_REGEX = /@\[[\w\s]+\]\((\d+)\)/g;

      try {
        setText('');
        setMentionedUserIds([]);
        const res = await dispatch(
          sendChatMessage({
            visitorId: conversation.visitor.id,
            conversationId: conversation.id,
            mentionedUserIds,
            content: {
              blocks: [
                {
                  type: ChatMessageBlockType.TEXT,
                  text: text.replaceAll(FULL_MENTION_REGEX, '@($1)').trim(),
                },
              ],
            },
          }),
        );
        unwrapResult(res);
        setIsTyping(false);
      } catch (err) {
        message.error(err.message || 'Failed to send message');
        setText(text);
        setMentionedUserIds(mentionedUserIds);
      }
    },
    [
      self,
      visitor,
      conversation?.id,
      conversation?.visitor.id,
      text,
      dispatch,
      mentionedUserIds,
    ],
  );

  const queryEmojis = useCallback((query: string) => {
    if (query.length === 0) return;

    const matches = Object.values(emojiData.emojis)
      .filter(emoji => emoji.id.includes(query.toLowerCase()))
      .slice(0, 5);

    return matches.map(e => ({ id: e.skins[0].native, display: e.id }));
  }, []);

  return (
    <div
      className={clsx(
        'SessionChatFooter',
        `SessionChatFooter--${size}`,
        fullWidth && `SessionChatFooter--fullWidth`,
        height === 'auto' && `SessionChatFooter--autoHeight`,
      )}
      style={{ height }}
    >
      <div className="SessionChatFooter__inner">
        {!!conversation && (
          <ConversationVisitorEmailReply visitor={conversation.visitor} />
        )}
        <MentionsInput
          className="SessionChatFooter__mentionsInput"
          forceSuggestionsAboveCursor
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder={'Type your message...'}
          autoFocus={autoFocus}
        >
          <Mention
            className="SessionChatFooter__mentionsHighlight"
            trigger="@"
            displayTransform={(_: string, displayName: string) => `@${displayName}`}
            data={mentionees}
            appendSpaceOnAdd
            renderSuggestion={(suggestion, _, highlightedDisplay) => {
              return (
                <div className="SessionChatFooter__mentionsSuggestion">
                  <UserAvatar
                    user={suggestion as User}
                    size={32}
                    className="SessionChatFooter__mentionsSuggestion-avatar"
                  />
                  {highlightedDisplay}
                </div>
              );
            }}
          />
          <Mention
            trigger=":"
            markup="__id__"
            regex={/($a)/}
            data={queryEmojis}
            appendSpaceOnAdd
            renderSuggestion={(suggestion, _, highlightedDisplay) => {
              return (
                <div className="SessionChatFooter__mentionsSuggestion">
                  <div>{suggestion.id}</div>
                  <div>:{highlightedDisplay}:</div>
                </div>
              );
            }}
          />
          <Mention
            trigger="/"
            data={cannedResponseList}
            markup="**[__display__](user:__content__)"
            appendSpaceOnAdd
            renderSuggestion={(suggestion, _, highlightedDisplay) => {
              return (
                <div className="SessionChatFooter__responseSuggestions">
                  <span className="SessionChatFooter__responseSuggestions__code">
                    {highlightedDisplay} -{' '}
                  </span>
                  <span className="SessionChatFooter__responseSuggestions__content">
                    {(suggestion as CannedResponse).content}
                  </span>
                </div>
              );
            }}
          />
        </MentionsInput>
        <div className="SessionChatFooter__actions">
          <SessionChatUploadButton handleImageChange={handleImageChange} />
          {conversation && (
            <>
              <SendMeetingBookerButton
                visitorId={conversation.visitor.id}
                conversationId={conversation.id}
              />
              {!conversation.visitor.contact?.email && (
                <SendInputCollectorButton
                  conversationId={conversation.id}
                  visitorId={conversation.visitor.id}
                  inputBlockType={ChatMessageBlockType.EMAIL_INPUT}
                />
              )}
            </>
          )}
          <Popover
            placement="topLeft"
            overlayClassName="SessionChatFooter__smileyPicker"
            visible={isSmileyPopoverVisible}
            onVisibleChange={setIsSmileyPopoverVisible}
            content={
              <Picker
                data={emojiData}
                onEmojiSelect={({ native }) => {
                  setIsSmileyPopoverVisible(false);
                  setText(t => t + native + ' ');
                }}
                theme="light"
                previewPosition="none"
              />
            }
          >
            <Button type="ghost" className="SessionChatFooter__emojis">
              <Icon icon="smiley" />
            </Button>
          </Popover>
          <Button
            className="SessionChatFooter__send"
            type="primary"
            onClick={handleKeyPress}
            disabled={!text}
            icon={<Icon icon="send" />}
          />
        </div>
      </div>
    </div>
  );
};
